import { isDevMode } from '@angular/core';
import { FormControl, FormControlOptions, ValidatorFn } from '@angular/forms';
import * as _ from 'lodash';
import { OuiValidationError } from 'omnium-ui/form-field/validation-error';
import { AdequationConfig, FicheConseil, FieldConfig } from 'projects/fiche-conseil-lib/src/public-api';
import { combineLatest } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { ValidatorsFactory } from 'src/fieldFormControl/validators/ValidatorsFactory';

export class FieldFormControl<FieldDataType = any, FicheConseilType = string> extends FormControl {
  // specific trick to handle nullable formControl : https://stackoverflow.com/questions/73867881/subclassing-formcontrol-triggers-ts2510-base-constructors-must-all-have-the-sa
  public override readonly value!: FieldDataType;

  public readonly formConfigSubject: BehaviorSubject<AdequationConfig | null>;
  public readonly ficheConseilSubject: BehaviorSubject<FicheConseil | null>;
  protected errorTextfieldErrors: OuiValidationError[] = [];

  private arrayItemSelector?: (arrayItem: any) => boolean;

  public dataKey: string;

  public ficheDataToFieldDataWrapper: ((ficheData: any) => FieldDataType) | undefined = undefined;
  public fieldDataToFicheDataWrapper: ((fieldData: FieldDataType) => FicheConseilType | null) | undefined = undefined;

  public params: { [key: string]: any } = {};

  public options: FormControlOptions | undefined;
  private isinitialized = false;
  formConfigService: FormConfigService;
  ficheConseilService: FicheConseilService;

  /**
   * Create a FieldFormControl instance.
   * @param formConfigService the service which provides the form configuration
   * @param ficheConseilService the service which provides the current fiche conseil
   * @param dataKey the key of the data in fiche conseil
   * @param defaultValue the default value of the FormControl.value
   * @param ficheDataToFieldDataWrapper a function to wrap the data from fiche conseil to a FieldDataType
   * @param fieldDataToFicheDataWrapper a function to wrap the FieldDataType to a FicheConseilType
   * @param arrayItemSelector a function to select the item in an array if dataKey target an array. First element of the array that validate this function is selected
   * @param options the options for the FormControl
   * @param params optionnal params to save in control (for validation for example)
   */
  public constructor(parameters: {
    formConfigService: FormConfigService;
    ficheConseilService: FicheConseilService;
    dataKey: string;
    defaultValue: FieldDataType;
    ficheDataToFieldDataWrapper?: ((ficheData: any) => FieldDataType) | undefined;
    fieldDataToFicheDataWrapper?: ((fieldData: FieldDataType) => FicheConseilType | null) | undefined;
    arrayItemSelector?: (arrayItem: any) => boolean;
    options?: FormControlOptions | undefined;
    params?: { [key: string]: any };
  }) {
    super(parameters.defaultValue, parameters.options);
    this.formConfigService = parameters.formConfigService;
    this.ficheConseilService = parameters.ficheConseilService;
    this.formConfigSubject = parameters.formConfigService.currentFormConfigSubject;
    this.ficheConseilSubject = parameters.ficheConseilService.currentFicheConseilSubject;
    this.options = parameters.options;
    this.dataKey = parameters.dataKey;
    this.arrayItemSelector = parameters.arrayItemSelector;

    if (parameters.params) {
      this.params = parameters.params;
    } else {
      this.params = {};
    }

    this.ficheDataToFieldDataWrapper = parameters.ficheDataToFieldDataWrapper;
    this.fieldDataToFicheDataWrapper = parameters.fieldDataToFicheDataWrapper;

    this.ficheConseilSubject.subscribe(() => {
      if (this.isinitialized) {
        this.onFicheConseilChange();
      }
    });

    this.formConfigSubject.subscribe(() => {
      if (this.isinitialized) {
        this.onFicheConfigChange();
      }
    });

    combineLatest([this.formConfigSubject, this.ficheConseilSubject]).subscribe(([formConfig, ficheConseil]) => {
      if (ficheConseil) {
        if (!this.isinitialized) {
          this.init();
          this.isinitialized = true;
        }
      }
    });

    super.registerOnChange((newValue: FieldDataType) => {
      this.onControlledDataEdited(newValue);
    });
  }
  protected init(): void {
    this.onInit();
  }

  protected onInit(): void {
    this.onFicheConfigChange();
    this.onFicheConseilChange();
    if (this.value !== null && this.value !== undefined) {
      // if there a value force validation
      this.markAsTouched();
    }
  }
  protected onFicheConseilChange(): void {
    // if new value is already current value do nothing
    if (this.ficheDataValue !== this.value) {
      super.setValue(this.ficheDataValue);
    }
    // do not update validity if is initializing and value is not set yet
    if (!(!this.isinitialized && (this.value == null || this.value == undefined))) {
      this.updateValueAndValidity();
    }
  }
  protected onFicheConfigChange(): void {
    this.applyValidators();
  }

  override setValue(
    value: FieldDataType,
    options?:
      | { onlySelf?: boolean; emitEvent?: boolean; emitModelToViewChange?: boolean; emitViewToModelChange?: boolean }
      | undefined
  ): void {
    //update field only if something has changed
    if (this.value !== value) {
      super.setValue(value, options);
      this.onControlledDataEdited(value);
    }
  }

  protected onControlledDataEdited(newValue: FieldDataType): void {
    try {
      if (newValue !== this.ficheDataValue) {
        const wrappedData = this.wrapFieldDataToFicheData(newValue);
        this.ficheConseilService.onFicheConseilDataEdit(this.dataKey, wrappedData, this.arrayItemSelector);
      }
    } catch (error) {
      if (isDevMode()) {
        console.log('error setting raw data in fiche conseil', this.dataKey);
      }
    }
  }

  public isVisible(): boolean {
    return this.formConfig?.isVisible ?? false;
  }

  /** data value in fiche conseil in DB */
  protected get formConfig(): FieldConfig | undefined {
    return this.formConfigSubject.value?.fieldConfigs.find(config => config.fieldKey === this.dataKey);
  }

  /** data value in fiche conseil in DB */
  protected get ficheDataValue(): FieldDataType {
    try {
      let rawData = this.getRawDataInFicheConseil();
      if (Array.isArray(rawData) && this.arrayItemSelector) {
        rawData = rawData.find(item => this.arrayItemSelector?.(item));
      }
      const wrapped = this.wrapFicheDataToFieldData(rawData);
      return wrapped;
    } catch (error) {
      if (isDevMode()) {
        console.log('error wrapping fiche data to field data', this.dataKey);
      }
    }
    return this.defaultValue;
  }

  private getRawDataInFicheConseil(): any | null {
    if (!this.ficheConseilSubject.value || !this.dataKey) {
      return null;
    }
    return _.get(this.ficheConseilSubject.value, this.dataKey);
  }

  protected wrapFicheDataToFieldData(ficheData: any): FieldDataType {
    if (this.ficheDataToFieldDataWrapper) {
      return this.ficheDataToFieldDataWrapper(ficheData);
    }
    // default wrapper to overload
    return ficheData as FieldDataType;
  }

  protected wrapFieldDataToFicheData(fieldData: FieldDataType): FicheConseilType | null {
    if (this.fieldDataToFicheDataWrapper) {
      return this.fieldDataToFicheDataWrapper(fieldData);
    }
    // default wrapper to overload
    return fieldData as unknown as FicheConseilType;
  }

  protected applyValidators(): void {
    if (!this.formConfig?.validators) {
      return;
    }
    const validatorConfs = this.formConfig?.validators;

    if (validatorConfs && validatorConfs.length > 0) {
      // we start to add validators from options
      const givenValidators: ValidatorFn | ValidatorFn[] | null | undefined = this.options?.validators;
      const validatorsToSet: ValidatorFn[] = [];
      if (givenValidators) {
        if (Array.isArray(givenValidators)) {
          validatorsToSet.push(...givenValidators);
        } else {
          validatorsToSet.push(givenValidators);
        }
      }
      const messagesToSet: OuiValidationError[] = [];
      for (const validatorConf of validatorConfs) {
        try {
          const validator = ValidatorsFactory.getValidator(
            this.dataKey,
            validatorConf.validatorKey,
            this.formConfigService
          );
          if (validator) {
            validatorsToSet.push(validator);
          }

          if (validatorConf?.errorMessage) {
            const code = this.dataKey + '_' + validatorConf.validatorKey;
            const errorTextField: OuiValidationError = {
              code,
              message: validatorConf?.errorMessage ?? 'donnée invalide',
            };
            messagesToSet.push(errorTextField);
          }
        } catch (error) {
          if (isDevMode()) {
            console.log(error);
          }
        }
      }

      this.errorTextfieldErrors = messagesToSet;
      super.setValidators(validatorsToSet);

      // do not update validity if is initializing and value is not set yet
      if (!(!this.isinitialized && (this.value == null || this.value == undefined))) {
        super.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    }
  }

  public getErrorTextfieldErrors(): OuiValidationError[] {
    return this.errorTextfieldErrors;
  }
}
